/**
 * Curated, royalty-free UK carp-fishing imagery.
 *
 * Why a manifest?
 *   - Single source of truth for any lake/fishery card that lacks a real photo.
 *   - Filters out the unrelated landscape/desert/portrait shots that crept in
 *     from older seed data and from auto-generated stock placeholders.
 *   - Future fishery uploads (or admin-curated galleries) simply override the
 *     `image_url` on the entity; the manifest only fills the gap.
 *
 * All images are sourced from Unsplash (free-use, no attribution required) and
 * have been visually vetted as misty UK-style lakes / fishing scenes / bivvy
 * setups / dawn waters. They colour-grade cleanly against the platform's
 * black + gold aesthetic.
 *
 * To swap an image: just replace the URL below. To add more: append to the
 * array — the deterministic picker spreads them evenly across all entities.
 */

const UNSPLASH_PARAMS = 'auto=format&fit=crop&q=80&w=1200';
const u = (id) => `https://images.unsplash.com/${id}?${UNSPLASH_PARAMS}`;

// Premium fallback — used by <FisheryImage> when nothing else loads.
// This is the "house" shot of the platform: a moody misty lake at dawn.
export const FISHERY_FALLBACK_IMAGE = u('photo-1604227694902-8d79311c6f19');

// Hero-grade shots (cinematic, used on large hero banners + lake detail covers)
export const FISHERY_HEROES = [
  u('photo-1604227694902-8d79311c6f19'), // misty lake fishing dawn
  u('photo-1738597602799-62422e1eeb8a'), // dawn lake silhouette
  u('photo-1758272960374-343f5eb7fdcd'), // friends camping by lake / firelight
  u('photo-1761393296074-4baf280c6406'), // carp angler holding a fish
];

// Card-grade rotation — used on lake/fishery grid cards. Visually consistent
// (atmospheric water, tree-lined banks, bivvy-friendly scenes).
const FISHERY_ROTATION = [
  u('photo-1604227694902-8d79311c6f19'), // misty UK lake at dawn
  u('photo-1738597602799-62422e1eeb8a'), // sunrise lake silhouette
  u('photo-1545450660-3378a7f3a364'),    // anglers on a still lake
  u('photo-1532724414795-8a873d116fab'), // calm tree-lined lake
  u('photo-1758272960374-343f5eb7fdcd'), // night-time lakeside campfire / bivvy
  u('photo-1761393296074-4baf280c6406'), // carp held by an angler
];

/**
 * Hash a string into a small positive integer (djb2). Deterministic so the
 * same lake always renders the same image across sessions/users.
 */
const hashSeed = (seed) => {
  const s = String(seed || '');
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // unsigned
  }
  return h;
};

/**
 * Pick a fishery image for a given seed (lake id, slug or name).
 * Will always return the same image for the same seed.
 *
 *   getFisheryImage('Linear Fisheries - St Johns') -> same URL every call
 */
export const getFisheryImage = (seed) => {
  if (!seed) return FISHERY_FALLBACK_IMAGE;
  const idx = hashSeed(seed) % FISHERY_ROTATION.length;
  return FISHERY_ROTATION[idx];
};

/**
 * Pick a hero image for the top-of-page banners (Lakes page hero, etc).
 */
export const getFisheryHero = (seed) => {
  if (!seed) return FISHERY_HEROES[0];
  const idx = hashSeed(seed) % FISHERY_HEROES.length;
  return FISHERY_HEROES[idx];
};

/**
 * Resolve a final image url for an entity:
 *   1. honour the entity's own image_url if present (real fishery photo / user upload)
 *   2. otherwise pick deterministically from the curated rotation
 *
 * Pass the lake/competition object as `entity` and a stable identifier as `seed`.
 */
export const resolveFisheryImage = (entity, seed) => {
  const url = entity && (entity.image_url || entity.cover_image_url || entity.photo_url);
  if (url && typeof url === 'string') return url;
  return getFisheryImage(seed || (entity && (entity.id || entity.slug || entity.name)));
};
